import { handleActions } from 'redux-actions';
import initialState, { channelMessagesInitial, searchInitial } from './states';
import { logoutSuccess } from 'ducks/auth/actions';
import { ENTITY_STATUS } from 'app/types/constants';
import {
  channelsRequest,
  channelsSuccess,
  channelsFailure,
  channelInfoSuccess,
  messagesRequest,
  messagesSuccess,
  messagesFailure,
  sendMessageRequest,
  sendMessageSuccess,
  sendMessageFailure,
  markAsReadSuccess,
  editMessageSuccess,
  deleteMessageSuccess,
  searchMessagesRequest,
  searchMessagesSuccess,
  searchMessagesFailure,
  clearSearch,
  newMessageReceived,
  messageReadReceived,
  messageEditedReceived,
  messageDeletedReceived,
} from './actions';
import { PaginationDirection } from './types';

const getChannelMessages = (state, channelID) =>
  state.messages[channelID] || channelMessagesInitial;

export default handleActions(
  {
    [channelsRequest](state) {
      return {
        ...state,
        loading: true,
      };
    },
    [channelsSuccess](state, { payload }) {
      return {
        ...state,
        channels: payload,
        loading: false,
      };
    },
    [channelsFailure](state) {
      return {
        ...state,
        loading: false,
      };
    },
    [channelInfoSuccess](state, { payload }) {
      return {
        ...state,
        channels: state.channels.map((channel) => {
          if (channel.channelID !== payload.channelID) return channel;
          const merged = { ...channel, ...payload };
          const currentLastID = channel.lastMessage?.messageID || 0;
          const incomingLastID = payload.lastMessage?.messageID || 0;
          merged.lastMessage = incomingLastID >= currentLastID ? payload.lastMessage : channel.lastMessage;
          return merged;
        }),
      };
    },
    [messagesRequest](state, { payload: { channelID } }) {
      const current = getChannelMessages(state, channelID);
      return {
        ...state,
        messages: {
          ...state.messages,
          [channelID]: {
            ...current,
            loading: true,
          },
        },
      };
    },
    [messagesSuccess](state, { payload }) {
      const { channelID, direction } = payload;
      const current = getChannelMessages(state, channelID);
      let list;

      if (direction === PaginationDirection.BEFORE || direction === PaginationDirection.AFTER) {
        const existingIDs = new Set(current.list.map((msg) => msg.messageID));
        const newMessages = payload.messages.filter((msg) => !existingIDs.has(msg.messageID));

        list = direction === PaginationDirection.BEFORE
          ? [...current.list, ...newMessages]
          : [...newMessages, ...current.list];
      } else {
        list = payload.messages;
      }

      return {
        ...state,
        messages: {
          ...state.messages,
          [channelID]: {
            list,
            hasBefore: direction === PaginationDirection.AFTER ? current.hasBefore : payload.hasBefore,
            hasAfter: direction === PaginationDirection.BEFORE ? current.hasAfter : payload.hasAfter,
            loading: false,
          },
        },
      };
    },
    [messagesFailure](state, { payload: { channelID } }) {
      const current = getChannelMessages(state, channelID);
      return {
        ...state,
        messages: {
          ...state.messages,
          [channelID]: {
            ...current,
            loading: false,
          },
        },
      };
    },
    [sendMessageRequest](state, { payload: { channelID } }) {
      return {
        ...state,
        sending: { ...state.sending, [channelID]: true },
      };
    },
    [sendMessageSuccess](state, { payload }) {
      const channelID = payload.channelID;
      const current = getChannelMessages(state, channelID);

      return {
        ...state,
        sending: { ...state.sending, [channelID]: false },
        channels: state.channels.map((channel) => {
          if (channel.channelID !== channelID) return channel;
          const currentLastID = channel.lastMessage?.messageID || 0;
          const newID = payload.messageID || 0;
          return {
            ...channel,
            lastMessage: newID >= currentLastID ? payload : channel.lastMessage,
          };
        }),
        messages: {
          ...state.messages,
          [channelID]: {
            ...current,
            list: [payload, ...current.list],
          },
        },
      };
    },
    [sendMessageFailure](state, { payload }) {
      const channelID = payload?.channelID;
      if (!channelID) return state;
      return {
        ...state,
        sending: { ...state.sending, [channelID]: false },
      };
    },
    [markAsReadSuccess](state, { payload: { channelID, messageID } }) {
      return {
        ...state,
        channels: state.channels.map((channel) =>
          channel.channelID === channelID
            ? { ...channel, lastReadMessageID: messageID }
            : channel
        ),
      };
    },
    [newMessageReceived](state, { payload }) {
      if (!payload || payload.channelID == null) return state;
      const { channelID } = payload;

      const channels = state.channels.map((channel) => {
        if (channel.channelID !== channelID) return channel;
        const currentLastID = channel.lastMessage?.messageID || 0;
        const newID = payload.messageID || 0;
        return {
          ...channel,
          unreadCount: (channel.unreadCount || 0) + 1,
          lastMessage: newID >= currentLastID ? payload : channel.lastMessage,
        };
      });

      let messages = state.messages;
      const current = state.messages[channelID];
      if (current && !current.hasAfter) {
        const exists = current.list.some((m) => m.messageID === payload.messageID);
        if (!exists) {
          messages = {
            ...state.messages,
            [channelID]: {
              ...current,
              list: [payload, ...current.list],
            },
          };
        }
      }

      return { ...state, channels, messages };
    },
    [editMessageSuccess](state, { payload }) {
      if (!payload || payload.channelID == null) return state;
      const { channelID, messageID } = payload;
      const current = state.messages[channelID];
      const channels = state.channels.map((channel) =>
        channel.channelID === channelID && channel.lastMessage?.messageID === messageID
          ? { ...channel, lastMessage: { ...channel.lastMessage, content: payload.content, edited: true } }
          : channel
      );
      const messages = current
        ? {
            ...state.messages,
            [channelID]: {
              ...current,
              list: current.list.map((m) =>
                m.messageID === messageID
                  ? { ...m, content: payload.content, edited: true }
                  : m
              ),
            },
          }
        : state.messages;
      return { ...state, channels, messages };
    },
    [deleteMessageSuccess](state, { payload: { channelID, messageID } }) {
      const current = state.messages[channelID];
      const channels = state.channels.map((channel) =>
        channel.channelID === channelID && channel.lastMessage?.messageID === messageID
          ? { ...channel, lastMessage: { ...channel.lastMessage, status: ENTITY_STATUS.DELETED, content: null, mediaAssetID: null } }
          : channel
      );
      const messages = current
        ? {
            ...state.messages,
            [channelID]: {
              ...current,
              list: current.list.map((m) =>
                m.messageID === messageID ? { ...m, status: ENTITY_STATUS.DELETED, content: null, mediaAssetID: null } : m
              ),
            },
          }
        : state.messages;
      return { ...state, channels, messages };
    },
    [messageEditedReceived](state, { payload }) {
      if (!payload || payload.channelID == null) return state;
      const { channelID, messageID } = payload;
      const current = state.messages[channelID];
      const channels = state.channels.map((channel) =>
        channel.channelID === channelID && channel.lastMessage?.messageID === messageID
          ? { ...channel, lastMessage: { ...channel.lastMessage, content: payload.content, edited: true } }
          : channel
      );
      const messages = current
        ? {
            ...state.messages,
            [channelID]: {
              ...current,
              list: current.list.map((m) =>
                m.messageID === messageID
                  ? { ...m, content: payload.content, edited: true }
                  : m
              ),
            },
          }
        : state.messages;
      return { ...state, channels, messages };
    },
    [messageDeletedReceived](state, { payload }) {
      if (!payload || payload.channelID == null || payload.messageID == null) return state;
      const { channelID, messageID } = payload;
      const current = state.messages[channelID];
      const channels = state.channels.map((channel) =>
        channel.channelID === channelID && channel.lastMessage?.messageID === messageID
          ? { ...channel, lastMessage: { ...channel.lastMessage, status: ENTITY_STATUS.DELETED, content: null, mediaAssetID: null } }
          : channel
      );
      const messages = current
        ? {
            ...state.messages,
            [channelID]: {
              ...current,
              list: current.list.map((m) =>
                m.messageID === messageID ? { ...m, status: ENTITY_STATUS.DELETED, content: null, mediaAssetID: null } : m
              ),
            },
          }
        : state.messages;
      return { ...state, channels, messages };
    },
    [messageReadReceived](state, { payload }) {
      if (!payload || payload.channelID == null || payload.messageID == null) return state;
      const { channelID, messageID } = payload;

      const current = state.messages[channelID];
      if (!current) return state;

      return {
        ...state,
        messages: {
          ...state.messages,
          [channelID]: {
            ...current,
            list: current.list.map((m) =>
              m.messageID <= messageID && !m.isRead ? { ...m, isRead: true } : m
            ),
          },
        },
      };
    },
    // SEARCH
    [searchMessagesRequest](state, { payload: { channelID, query } }) {
      return {
        ...state,
        search: { channelID, query, results: [], loading: true },
      };
    },
    // SEARCH
    [searchMessagesSuccess](state, { payload: { channelID, query, results } }) {
      // Guard against stale responses arriving after a newer query or a clear
      if (state.search.channelID !== channelID || state.search.query !== query) return state;
      return {
        ...state,
        search: { ...state.search, results, loading: false },
      };
    },
    // SEARCH
    [searchMessagesFailure](state) {
      return {
        ...state,
        search: { ...state.search, loading: false },
      };
    },
    // SEARCH
    [clearSearch](state) {
      return { ...state, search: searchInitial };
    },
    [logoutSuccess]() {
      return initialState;
    },
  },
  initialState
);
